import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { ArrowUpRightIcon } from "@heroicons/react/24/outline";

const projects = [
  {
    title: "Commercial Complex",
    category: "Structural Design",
    location: "Gurugram",
    image: "https://images.unsplash.com/photo-1769556669134-fe947b53bdbf?w=600&q=80",
    description: "A modern commercial complex with state-of-the-art structural design.",
  },
  {
    title: "Residential Township",
    category: "Civil Engineering",
    location: "Noida",
    image: "https://images.unsplash.com/photo-1674821770946-4f774b1907d7?w=600&q=80",
    description: "Large-scale residential township with integrated infrastructure.",
  },
  {
    title: "Industrial Facility",
    category: "Turnkey Construction",
    location: "Faridabad",
    image: "https://images.unsplash.com/photo-1760001868264-3c7deb4c975b?w=600&q=80",
    description: "Complete turnkey industrial facility with modern amenities.",
  },
  {
    title: "Luxury Apartments",
    category: "Architectural Design",
    location: "Delhi",
    image: "https://images.unsplash.com/photo-1632400990400-416d5460f337?w=600&q=80",
    description: "Premium luxury apartments with contemporary architectural design.",
  },
  {
    title: "Office Tower",
    category: "Structural Design",
    location: "Ghaziabad",
    image: "https://images.unsplash.com/photo-1768822854459-725b1105eb15?w=600&q=80",
    description: "High-rise office tower with innovative structural solutions.",
  },
  {
    title: "Mixed-Use Development",
    category: "Civil Engineering",
    location: "Agra",
    image: "https://images.unsplash.com/photo-1653118554222-23fff03cccc3?w=600&q=80",
    description: "Integrated mixed-use development combining retail and residential.",
  },
  {
    title: "Hospital Complex",
    category: "Turnkey Construction",
    location: "Mathura",
    image: "https://images.unsplash.com/photo-1769556353286-b7f50da70d0c?w=600&q=80",
    description: "Multi-specialty hospital with complete medical infrastructure.",
  },
  {
    title: "Educational Institute",
    category: "Architectural Design",
    location: "Bhopal",
    image: "https://images.unsplash.com/photo-1645597459770-4d42ef850a18?w=600&q=80",
    description: "Modern educational campus with sustainable design principles.",
  },
];

const categories = ["All", "Structural Design", "Civil Engineering", "Architectural Design", "Turnkey Construction"];

const ProjectsPage = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredProjects = selectedCategory === "All" 
    ? projects 
    : projects.filter(p => p.category === selectedCategory);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-zinc-50"
    >
      <Navbar />
      <main className="pt-20">
        {/* Page Header */}
        <section className="py-20 bg-slate-900 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <span className="text-orange-500 text-sm font-semibold uppercase tracking-wider font-manrope">
                Portfolio
              </span>
              <h1 className="font-oswald text-4xl sm:text-5xl lg:text-6xl font-bold text-white mt-4">
                Featured <span className="text-orange-500">Projects</span>
              </h1>
              <p className="text-slate-300 mt-6 text-lg font-manrope max-w-3xl mx-auto">
                Explore our diverse portfolio of successful projects across
                residential, commercial, and industrial sectors.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Filter */}
        <section className="py-8 bg-white border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap justify-center gap-4">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-2 font-manrope text-sm font-medium transition-all ${
                    selectedCategory === category
                      ? "bg-slate-900 text-white"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                  data-testid={`filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Projects Grid */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects.map((project, index) => (
                <motion.div
                  key={project.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="group relative overflow-hidden bg-white border border-slate-100 hover:shadow-xl transition-all"
                  data-testid={`project-card-${index}`}
                >
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent opacity-60 group-hover:opacity-80 transition-opacity" />
                    
                    {/* Hover Arrow */}
                    <div className="absolute top-4 right-4 w-10 h-10 bg-orange-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <ArrowUpRightIcon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <span className="text-orange-500 text-sm font-manrope">
                      {project.category}
                    </span>
                    <h3 className="font-oswald text-xl font-semibold text-slate-900 mt-1">
                      {project.title}
                    </h3>
                    <p className="text-slate-500 text-sm font-manrope mt-1">
                      {project.location}
                    </p>
                    <p className="text-slate-600 text-sm font-manrope mt-3">
                      {project.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-zinc-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-oswald text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
              Have a Project in Mind?
            </h2>
            <p className="text-slate-600 font-manrope mb-8">
              Let's discuss how we can help bring your vision to life.
            </p>
            <Link
              to="/contact"
              className="inline-block bg-orange-500 text-white px-8 py-4 font-manrope font-semibold uppercase tracking-wider text-sm hover:bg-orange-600 transition-colors"
              data-testid="projects-cta"
            >
              Start Your Project
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </motion.div>
  );
};

export default ProjectsPage;
